import axios, { type AxiosRequestConfig } from 'axios'
import { env } from '@/config/env'
import { buildWebStorage, setupCache } from 'axios-cache-interceptor'
import router from '@/router'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { ApiError } from '@/modules/core/models/api-error.class.ts'
import { ApiErrorType } from '@/modules/core/enums/api-error-type.ts'

const isProduction = import.meta.env.MODE === 'production'
const baseUrl: string = env.apiBaseUrl + '/v1'

const TTL = {
  SHORT: 1 * 1000,
  MEDIUM: 15 * 60 * 1000,
  LONG: 24 * 60 * 60 * 1000,
  STOCK_PRICE: 5 * 1000,
  USER_PROFILE: 5 * 60 * 1000,
}

// Create axios instance
const axiosInstance = axios.create({
  baseURL: baseUrl,
  timeout: 60 * 1000,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

// Setup cache with axios instance
const api = setupCache(axiosInstance, {
  ttl: TTL.SHORT,
  storage: buildWebStorage(localStorage, 'axios-cache:'),
  interpretHeader: false,
  methods: ['get'],
  generateKey: req => {
    const url = req.url || ''
    if (/(auth|login|logout|csrf)/i.test(url)) {
      return ''
    }
    return `${req.method}:${req.url}:${JSON.stringify(req.params)}`
  },
  debug: !isProduction ? console.debug : undefined,
})

// Request interceptor for authentication only
api.interceptors.request.use(
  async config => {
    const auth = useAuthStore()
    const token = auth.accessToken

    // Add Bearer token
    if (token?.token) {
      config.headers = config.headers || {}
      config.headers.Authorization = `Bearer ${token.token}`
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  response => response,

  async error => {
    const originalRequest = error.config
    const apiError = ApiError.fromAxiosError(error)

    switch (apiError.type) {
      case ApiErrorType.AUTHENTICATION_ERROR:
        if (!originalRequest._retry) {
          originalRequest._retry = true
          await router.push({ name: 'auth.login' })
        }
        break
    }

    logError('StarsaversApi', 'API Error:', {
      type: apiError.type,
      status: apiError.statusCode,
      message: apiError.message,
      url: originalRequest?.url,
      retryable: apiError.retryable,
    })

    return Promise.reject(apiError)
  }
)

// Helper methods
export const starsaversApi = {
  get: async <T>(url: string, config?: AxiosRequestConfig) => {
    const response = await api.get<T>(url, {
      cache: {},
      ...config,
    })
    return response.data
  },

  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig) => {
    const response = await api.post<T>(url, data, config)
    return response.data
  },

  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig) => {
    const response = await api.put<T>(url, data, config)
    return response.data
  },

  patch: async <T>(url: string, data?: any, config?: AxiosRequestConfig) => {
    const response = await api.patch<T>(url, data, config)
    return response.data
  },

  delete: async <T>(url: string, config?: AxiosRequestConfig) => {
    const response = await api.delete<T>(url, config)
    return response.data
  },
}

export default api
